/**
 * Water Balance API Service
 *
 * This service provides methods for interacting with water balance
 * backend endpoints including status, history, projections, reset,
 * and farm overview.
 */

import apiClient from './apiClient';
import type {
  WaterBalanceStatus,
  WaterBalanceHistoryResponse,
  WaterBalanceProjectionsResponse,
  WaterBalanceResetRequest,
  WaterBalanceDaily,
  FarmWaterBalanceOverview,
  WaterBalanceRecalculateRequest,
  WaterBalanceRecalculateResponse,
} from '@/types/waterBalance';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build query string from parameters
 */
const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ============================================================================
// Water Balance API
// ============================================================================

/**
 * Get current water balance status for a sector.
 *
 * @param sectorId - The sector ID
 * @returns Current water balance status
 */
export const getWaterBalanceStatus = async (
  sectorId: number
): Promise<WaterBalanceStatus> => {
  return apiClient.get<WaterBalanceStatus>(`/water-balance/sectors/${sectorId}`);
};

/**
 * Get historical water balance records for a sector.
 *
 * @param sectorId - The sector ID
 * @param startDate - Start date for history (ISO date string)
 * @param endDate - End date for history (ISO date string)
 * @returns Historical water balance records with summary statistics
 */
export const getWaterBalanceHistory = async (
  sectorId: number,
  startDate: string,
  endDate: string
): Promise<WaterBalanceHistoryResponse> => {
  const queryString = buildQueryString({ start_date: startDate, end_date: endDate });
  return apiClient.get<WaterBalanceHistoryResponse>(
    `/water-balance/sectors/${sectorId}/history${queryString}`
  );
};

/**
 * Get projected water balance for a sector using weather forecast.
 *
 * @param sectorId - The sector ID
 * @param daysAhead - Number of days to project (default: 14, max: 30)
 * @param includePlannedIrrigations - Whether to include planned irrigations from approved schedules
 * @returns Projected water balance for future days
 */
export const getWaterBalanceProjections = async (
  sectorId: number,
  daysAhead?: number,
  includePlannedIrrigations?: boolean
): Promise<WaterBalanceProjectionsResponse> => {
  const queryString = buildQueryString({
    days_ahead: daysAhead,
    include_planned_irrigations: includePlannedIrrigations
  });
  return apiClient.get<WaterBalanceProjectionsResponse>(
    `/water-balance/sectors/${sectorId}/projections${queryString}`
  );
};

/**
 * Manually reset water balance for a sector.
 *
 * This allows manual adjustment of soil water depletion after
 * soil sampling or other field measurements.
 *
 * @param sectorId - The sector ID
 * @param newDepletionMm - New depletion value in mm
 * @param reason - Reason for reset (1-500 characters)
 * @param targetDate - Target date for reset (ISO date string, optional, defaults to today)
 * @returns The updated water balance daily record
 */
export const resetWaterBalance = async (
  sectorId: number,
  newDepletionMm: number,
  reason: string,
  targetDate?: string
): Promise<WaterBalanceDaily> => {
  const requestBody: WaterBalanceResetRequest = {
    new_depletion_mm: newDepletionMm,
    reason,
  };
  const queryString = buildQueryString({ target_date: targetDate });
  return apiClient.post<WaterBalanceDaily>(
    `/water-balance/sectors/${sectorId}/reset${queryString}`,
    requestBody
  );
};

/**
 * Get water balance overview for all sectors in a farm.
 *
 * Returns a summary of water balance status for all sectors
 * within a farm, useful for dashboard views.
 *
 * @param farmId - The farm ID
 * @returns Farm water balance overview
 */
export const getFarmWaterBalanceOverview = async (
  farmId: string
): Promise<FarmWaterBalanceOverview> => {
  return apiClient.get<FarmWaterBalanceOverview>(`/water-balance/farms/${farmId}/overview`);
};

/**
 * Force recalculation of water balance for a date range.
 *
 * This is useful when:
 * - Manual irrigation events are added and need to be reflected in water balance
 * - Weather data is backfilled or corrected
 * - Irrigation execution data is updated
 * - Any other input data changes that require recalculation
 *
 * The endpoint will recalculate water balance for each day in the specified range,
 * including both scheduled and manual irrigation events.
 *
 * @param sectorId - The sector ID
 * @param startDate - Start date for recalculation (ISO date string in YYYY-MM-DD format)
 * @param endDate - End date for recalculation (ISO date string in YYYY-MM-DD format)
 * @param asyncMode - If true, use task queue; if false, execute synchronously (default: true)
 * @returns Recalculation results including success/failure counts, records, and errors
 */
export const recalculateWaterBalance = async (
  sectorId: number,
  startDate: string,
  endDate: string,
  asyncMode: boolean = true
): Promise<WaterBalanceRecalculateResponse | any> => {
  const requestBody: WaterBalanceRecalculateRequest = {
    start_date: startDate,
    end_date: endDate,
  };
  const queryString = asyncMode ? '?async_mode=true' : '';
  return apiClient.post<any>(
    `/water-balance/sectors/${sectorId}/recalculate${queryString}`,
    requestBody
  );
};

export interface FarmWaterBalanceRecalculateRequest {
  start_date?: string;
  end_date?: string;
  force?: boolean;
}

export interface TaskEnqueueResponse {
  task_id: string;
  status: string;
  message: string;
}

/**
 * Trigger a farm-wide water balance recalculation. Admin/Owner only on
 * the backend. Omit dates to use the active season's full range.
 * Returns a task id that can be polled via the task status endpoint.
 *
 * Backend returns 409 if a recalculation is already in flight for the
 * farm — the UI should surface that as a "wait, one is already running"
 * message rather than retry automatically.
 */
export const recalculateFarmWaterBalance = async (
  farmId: string,
  payload: FarmWaterBalanceRecalculateRequest = {}
): Promise<TaskEnqueueResponse> => {
  return apiClient.post<TaskEnqueueResponse>(
    `/water-balance/farms/${farmId}/recalculate`,
    { force: true, ...payload }
  );
};

// ============================================================================
// Export all API functions
// ============================================================================

const waterBalanceApi = {
  getWaterBalanceStatus,
  getWaterBalanceHistory,
  getWaterBalanceProjections,
  resetWaterBalance,
  getFarmWaterBalanceOverview,
  recalculateWaterBalance,
  recalculateFarmWaterBalance,
};

export default waterBalanceApi;
